import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "../components/ui/button";
import { Download, Copy, FilePlus2, Loader2, Check, Pencil, X, FileText, Presentation, Newspaper } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DocumentViewer = ({ content, title, generating, onReset, onContentChange, docType, onGenerateArticle }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [downloadingPptx, setDownloadingPptx] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEditing = () => {
    setDraft(content);
    setEditing(true);
  };

  const saveDraft = () => {
    onContentChange(draft);
    setEditing(false);
    toast.success("Promjene su spremljene!");
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(`${API}/pdf`, { title, content }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/[^\w\sčćžšđČĆŽŠĐ-]/g, "").trim() || "dokument"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF je preuzet!");
    } catch {
      toast.error("Preuzimanje PDF-a nije uspjelo. Pokušajte ponovno.");
    } finally {
      setDownloading(false);
    }
  };

  const downloadDocx = async () => {
    setDownloadingDocx(true);
    try {
      const res = await axios.post(`${API}/docx`, { title, content }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/[^\w\sčćžšđČĆŽŠĐ-]/g, "").trim() || "dokument"}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Word dokument je preuzet!");
    } catch {
      toast.error("Preuzimanje Word dokumenta nije uspjelo. Pokušajte ponovno.");
    } finally {
      setDownloadingDocx(false);
    }
  };

  const downloadPptx = async () => {
    setDownloadingPptx(true);
    toast.info("AI izrađuje prezentaciju, može potrajati do minute…");
    try {
      const res = await axios.post(
        `${API}/pptx`,
        { title, content, doc_type: docType },
        { responseType: "blob", timeout: 180000 }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/[^\w\sčćžšđČĆŽŠĐ-]/g, "").trim() || "prezentacija"}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PowerPoint prezentacija je preuzeta!");
    } catch (e) {
      let msg = "Izrada prezentacije nije uspjela. Pokušajte ponovno.";
      try {
        if (e?.response?.data instanceof Blob) {
          const j = JSON.parse(await e.response.data.text());
          if (j.detail) msg = j.detail;
        }
      } catch {}
      toast.error(msg);
    } finally {
      setDownloadingPptx(false);
    }
  };

  const copyText = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    toast.success("Tekst je kopiran!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4" data-testid="document-viewer">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-3.5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              <span data-testid="generating-status">AI pametno priprema vaš dokument…</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4 text-green-600" />
              <span data-testid="done-status">Dokument je spreman!</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {editing ? (
            <>
              <Button
                data-testid="save-edit-btn"
                size="sm"
                className="rounded-xl bg-green-600 hover:bg-green-700 text-white"
                onClick={saveDraft}
              >
                <Check className="w-4 h-4 mr-1.5" />
                Spremi promjene
              </Button>
              <Button
                data-testid="cancel-edit-btn"
                variant="outline"
                size="sm"
                className="rounded-xl"
                onClick={() => setEditing(false)}
              >
                <X className="w-4 h-4 mr-1.5" />
                Odustani
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="edit-btn"
                variant="outline"
                size="sm"
                className="rounded-xl"
                onClick={startEditing}
                disabled={generating || !content}
              >
                <Pencil className="w-4 h-4 mr-1.5" />
                Uredi
              </Button>
              <Button
                data-testid="copy-btn"
                variant="outline"
                size="sm"
                className="rounded-xl"
                onClick={copyText}
                disabled={generating || !content}
              >
                {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                Kopiraj
              </Button>
              <Button
                data-testid="download-pdf-btn"
                size="sm"
                className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
                onClick={downloadPdf}
                disabled={generating || !content || downloading}
              >
                {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
                Preuzmi PDF
              </Button>
              <Button
                data-testid="download-docx-btn"
                size="sm"
                className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
                onClick={downloadDocx}
                disabled={generating || !content || downloadingDocx}
              >
                {downloadingDocx ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
                Preuzmi Word
              </Button>
              <Button
                data-testid="new-document-btn"
                variant="outline"
                size="sm"
                className="rounded-xl"
                onClick={onReset}
                disabled={generating}
              >
                <FilePlus2 className="w-4 h-4 mr-1.5" />
                Novi dokument
              </Button>
            </>
          )}
        </div>
      </div>

      {!editing && !generating && content && (docType === "priprema" || docType === "izvanucionicka") && (
        <div
          data-testid="extras-panel"
          className="flex flex-wrap items-center gap-3 bg-blue-50/70 rounded-2xl border border-blue-100 px-5 py-3.5"
        >
          <span className="text-sm font-semibold text-slate-700">Dodatne mogućnosti:</span>
          <Button
            data-testid="generate-pptx-btn"
            variant="outline"
            size="sm"
            className="rounded-xl bg-white"
            onClick={downloadPptx}
            disabled={downloadingPptx}
          >
            {downloadingPptx ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Presentation className="w-4 h-4 mr-1.5" />}
            {downloadingPptx ? "Izrađujem prezentaciju…" : "Izradi PowerPoint"}
          </Button>
          <Button
            data-testid="generate-article-btn"
            variant="outline"
            size="sm"
            className="rounded-xl bg-white"
            onClick={onGenerateArticle}
          >
            <Newspaper className="w-4 h-4 mr-1.5" />
            Napiši članak za web
          </Button>
          <span className="text-xs text-slate-400 basis-full sm:basis-auto">
            Prezentaciju i članak izrađuje AI na temelju ovog dokumenta.
          </span>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-md mx-auto w-full p-6 sm:p-12 min-h-[400px]">
        {editing ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-400">
              Uredite tekst dokumenta. Oznake poput # (naslov), ** (podebljano) i | (tablica) određuju izgled dokumenta, najbolje ih je ostaviti kako jesu.
            </p>
            <textarea
              data-testid="edit-textarea"
              className="w-full min-h-[600px] rounded-xl border border-slate-200 p-4 text-sm leading-relaxed text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>
        ) : (
          <div className="doc-content" data-testid="document-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            {generating && <span className="typing-cursor" />}
          </div>
        )}
      </div>
    </div>
  );
};
